import React, { useState, useRef, useEffect, useMemo } from 'react';
import './BulkRegistrationImport.css';
import SvgIcon from '../../components/SvgIcon';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Interactive Searchable & Fuzzy-Matching Dropdown for Course Schedule Selection
 */
const SearchableScheduleSelect = ({
  rowId,
  schedule,
  course,
  schedules = [],
  projectCode = null,
  tags = [],
  courseTagAccess = [],
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle focus on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Determine allowed course IDs if a valid project tag is present
  const allowedCourseIds = useMemo(() => {
    const cleanTag = (projectCode || '').trim().toUpperCase();
    if (!cleanTag) return null; // No project tag constraint

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const foundTag = (tags || []).find(t => (t.code || t.TagCode || '').toUpperCase() === cleanTag);
    if (!foundTag) return null;

    // Check validity and expiry: must be active, not expired, and have an expiry date
    if (foundTag.isValid !== undefined) {
      if (!foundTag.isValid) return null;
    } else {
      const exp = foundTag.expiryDate || foundTag.ExpiryDate;
      if (!exp) return null;
      const expDate = new Date(exp);
      if (expDate < today) return null; // Expired
      if (foundTag.isActive === 0 || foundTag.IsActive === 0) return null;
    }

    const tagId = foundTag.id || foundTag.UserTagID;
    const mapped = (courseTagAccess || [])
      .filter(cta => String(cta.tagId || cta.UserTagID) === String(tagId))
      .map(cta => cta.courseId || cta.CourseID);

    return new Set(mapped);
  }, [projectCode, tags, courseTagAccess]);

  // Available schedules restricted by tag if applicable
  const availableSchedules = useMemo(() => {
    if (!allowedCourseIds) return schedules;
    return schedules.filter(s => allowedCourseIds.has(s.courseId));
  }, [schedules, allowedCourseIds]);

  // Search & Fuzzy Matching Filter
  const filteredSchedules = useMemo(() => {
    if (!searchTerm.trim()) {
      return availableSchedules;
    }
    const cleanQuery = searchTerm.toLowerCase().trim();
    const queryTokens = cleanQuery.split(/\s+/).filter(Boolean);

    return availableSchedules.filter(s => {
      const idStr = String(s.courseId || '');
      const schedIdStr = String(s.scheduleId || '');
      const name = (s.courseName || '').toLowerCase();
      const start = (s.startDate || '').toLowerCase();
      const end = (s.endDate || '').toLowerCase();
      const combined = `${idStr} ${schedIdStr} ${name} ${start} ${end}`;

      // 1. Direct or substring match
      if (combined.includes(cleanQuery)) return true;

      // 2. All tokens match anywhere in combined string
      const allTokensMatch = queryTokens.every(token => combined.includes(token));
      if (allTokensMatch) return true;

      // 3. Subsequence / fuzzy abbreviation match
      return queryTokens.some(token => {
        if (token.length < 2) return false;
        let tokenIdx = 0;
        for (let i = 0; i < name.length && tokenIdx < token.length; i++) {
          if (name[i] === token[tokenIdx]) tokenIdx++;
        }
        return tokenIdx === token.length;
      });
    });
  }, [availableSchedules, searchTerm]);

  const handleSelectOption = (schedId) => {
    onChange(schedId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const hasWarning = !schedule;

  return (
    <div className="searchable-schedule-container" ref={containerRef}>
      {/* Trigger Button */}
      <button
        type="button"
        id={`schedule-select-btn-${rowId}`}
        className={`searchable-schedule-trigger ${hasWarning ? 'has-warning' : 'is-selected'}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title="Click to search or change Course & Schedule"
      >
        <div className="schedule-trigger-content">
          {schedule ? (
            <div className="trigger-selected">
              <div className="trigger-course-line">
                {schedule?.scheduleId && (
                  <span className="trigger-cid-badge">ID: {schedule.scheduleId}</span>
                )}
                <span className="trigger-course-name" title={course?.courseName || schedule.courseName}>
                  {course?.courseName || schedule.courseName}
                </span>
              </div>
              <div className="trigger-date-line">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: '-1px' }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span>{schedule.startDate} to {schedule.endDate}</span>
              </div>
            </div>
          ) : (
            <div className="trigger-warning" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                backgroundColor: '#e0e7ff',
                color: '#4338ca',
                flexShrink: 0
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </span>
              <span style={{ fontWeight: 600, color: '#475569', fontSize: '0.84rem' }}>
                Select Course Schedule...
              </span>
            </div>
          )}
        </div>
        <div className="schedule-trigger-chevron">
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div className="searchable-schedule-popover" role="listbox">
          {/* Search Box Header */}
          <div className="schedule-search-header">
            <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="schedule-search-input"
              placeholder="Search course ID, title, or dates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="schedule-options-scroll">
            <div className="schedule-options-divider">
              <span>AVAILABLE COURSE SCHEDULES ({filteredSchedules.length})</span>
            </div>

            {/* List of Schedules */}
            {filteredSchedules.length === 0 ? (
              <div className="no-schedules-found">
                {searchTerm.trim()
                  ? `No course schedules match "${searchTerm.trim()}"`
                  : 'No matched course schedule currently'}
              </div>
            ) : (
              filteredSchedules.map(s => {
                const isSelected = String(schedule?.scheduleId) === String(s.scheduleId);
                const isFull = (s.enrolledCount || 0) >= (s.maxCapacity || 10);

                return (
                  <div
                    key={s.scheduleId}
                    className={`schedule-option-item ${isSelected ? 'is-active' : ''}`}
                    onClick={() => handleSelectOption(s.scheduleId)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="option-main">
                      <div className="option-title-row">
                        <span className="option-id-badge">ID: {s.scheduleId}</span>
                        <span className="option-name">{s.courseName}</span>
                      </div>
                      <div className="option-meta">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          <span>{s.startDate} to {s.endDate}</span>
                        </span>
                        <span className={`option-capacity-pill ${isFull ? 'full' : 'available'}`}>
                          {s.enrolledCount || 0}/{s.maxCapacity || 10} {isFull ? '(Waitlist)' : 'Enrolled'}
                        </span>
                      </div>
                    </div>
                    {isSelected && <span className="option-check">✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Interactive Searchable & Styled Dropdown for Special Project Tag Selection
 * Follows identical design language and CSS classes as SearchableScheduleSelect
 */
const SearchableProjectTagSelect = ({
  value,
  tags = [],
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const cleanVal = (value || '').trim().toUpperCase();

  // Filter only unexpired and active tags
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const availableTags = useMemo(() => {
    return (tags || []).filter(t => {
      // If backend provided isValid flag, strictly respect it
      if (t.isValid !== undefined) {
        return Boolean(t.isValid);
      }
      const isActive = t.isActive !== 0 && t.IsActive !== 0;
      const exp = t.expiryDate || t.ExpiryDate;
      // Per system rules, tags without ExpiryDate cannot be considered valid
      if (!isActive || !exp) return false;
      const expDate = new Date(exp);
      return expDate >= today;
    });
  }, [tags, today]);

  const filteredTags = useMemo(() => {
    if (!searchTerm.trim()) return availableTags;
    const q = searchTerm.toLowerCase();
    return availableTags.filter(t => {
      const code = (t.code || t.TagCode || '').toLowerCase();
      const name = (t.name || t.TagName || '').toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [availableTags, searchTerm]);

  const selectedTag = availableTags.find(t => (t.code || t.TagCode || '').toUpperCase() === cleanVal);

  const handleSelectOption = (code) => {
    onChange(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-schedule-container" ref={containerRef} style={{ maxWidth: '100%' }}>
      {/* Trigger Button */}
      <button
        type="button"
        className="searchable-schedule-trigger is-selected"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{ minHeight: '44px' }}
      >
        <div className="schedule-trigger-content">
          {cleanVal ? (
            <div className="trigger-selected">
              <div className="trigger-course-line">
                <span className="trigger-course-name" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {cleanVal}
                </span>
              </div>
              {selectedTag?.name && (
                <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
                  {selectedTag.name || selectedTag.TagName}
                </div>
              )}
            </div>
          ) : (
            <div className="trigger-account-only">
              <span className="account-only-badge" style={{ backgroundColor: '#f1f5f9', color: '#475569' }}>None</span>
              <span className="trigger-title" style={{ color: '#64748b' }}>No Special Project Code</span>
            </div>
          )}
        </div>
        <div className="schedule-trigger-chevron">
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div className="searchable-schedule-popover" role="listbox" style={{ width: '100%', maxHeight: '280px' }}>
          {/* Search Box Header */}
          <div className="schedule-search-header">
            <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="schedule-search-input"
              placeholder="Search active project code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="schedule-options-scroll">
            {/* None Option */}
            <div
              className={`schedule-option-item ${!cleanVal ? 'is-active' : ''}`}
              onClick={() => handleSelectOption('')}
              role="option"
              aria-selected={!cleanVal}
            >
              <div className="option-main">
                <div className="option-title-row">
                  <span className="option-name" style={{ color: '#64748b', fontStyle: 'italic' }}>None (No Special Project Code)</span>
                </div>
              </div>
              {!cleanVal && <span className="option-check">✓</span>}
            </div>

            <div className="schedule-options-divider">
              <span>AVAILABLE SPECIAL PROJECT CODES ({filteredTags.length})</span>
            </div>

            {/* List of Available Tags */}
            {filteredTags.length === 0 ? (
              <div className="no-schedules-found">
                {searchTerm.trim()
                  ? `No active project code matches "${searchTerm.trim()}"`
                  : 'No active project code currently'}
              </div>
            ) : (
              filteredTags.map(t => {
                const code = (t.code || t.TagCode || '').toUpperCase();
                const isSelected = cleanVal === code;

                return (
                  <div
                    key={t.id || code}
                    className={`schedule-option-item ${isSelected ? 'is-active' : ''}`}
                    onClick={() => handleSelectOption(code)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="option-main">
                      <div className="option-title-row">
                        <span className="option-name" style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                          {code}
                        </span>
                      </div>
                      {(t.name || t.TagName) && (
                        <div className="option-meta">
                          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>{t.name || t.TagName}</span>
                        </div>
                      )}
                    </div>
                    {isSelected && <span className="option-check">✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Interactive Searchable & Filterable Dropdown for Organisation Selection
 * Includes "Cannot find organisation? Create a new one" functionality
 */
const SearchableOrgSelect = ({
  rowId,
  currentOrg,
  organisations = [],
  onSelectOrg,
  onCreateNewOrg
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSector, setNewOrgSector] = useState('Government');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [openUpwards, setOpenUpwards] = useState(false);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsCreating(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle positioning & focus on open
  useEffect(() => {
    if (isOpen) {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        // Only flip upward if space below is less than 130px and there is plenty of space above
        if (spaceBelow < 130 && rect.top > 250) {
          setOpenUpwards(true);
        } else {
          setOpenUpwards(false);
        }
      }
      setTimeout(() => {
        if (searchInputRef.current) searchInputRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  const filteredOrgs = useMemo(() => {
    if (!searchTerm.trim()) return organisations;
    const clean = searchTerm.toLowerCase().trim();
    return organisations.filter(o =>
      (o.name || o.OrganisationName || '').toLowerCase().includes(clean) ||
      (o.sector || o.Sector || '').toLowerCase().includes(clean)
    );
  }, [organisations, searchTerm]);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) {
      setCreateError('Please enter organisation name');
      return;
    }
    setCreateLoading(true);
    setCreateError('');
    try {
      await onCreateNewOrg(rowId, newOrgName.trim(), newOrgSector);
      setIsCreating(false);
      setIsOpen(false);
      setNewOrgName('');
    } catch (err) {
      setCreateError(err.message || 'Failed to create organisation');
    } finally {
      setCreateLoading(false);
    }
  };

  const selectedName = currentOrg?.name || currentOrg?.rawInput || '';
  const isSelected = Boolean(currentOrg?.id || currentOrg?.name);

  return (
    <div className="searchable-schedule-container" ref={containerRef} style={{ width: '100%', minWidth: '220px', position: 'relative' }}>
      <button
        type="button"
        className={`searchable-schedule-trigger ${!isSelected ? 'has-warning' : 'is-selected'}`}
        onClick={() => {
          setIsOpen(!isOpen);
          setIsCreating(false);
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.45rem 0.75rem',
          borderRadius: '6px',
          border: isSelected ? '1px solid #cbd5e1' : '1px solid #f59e0b',
          backgroundColor: isSelected ? '#ffffff' : '#fffbeb',
          fontSize: '0.82rem',
          cursor: 'pointer',
          textAlign: 'left',
          boxShadow: isOpen ? '0 0 0 2px rgba(79, 70, 229, 0.15)' : 'none'
        }}
      >
        <span style={{
          color: isSelected ? '#1e293b' : '#b45309',
          fontWeight: isSelected ? 600 : 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1
        }}>
          {selectedName || 'Select Organisation...'}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 20 20"
          fill="currentColor"
          style={{
            flexShrink: 0,
            color: '#64748b',
            marginLeft: '6px',
            transform: isOpen ? (openUpwards ? 'rotate(0deg)' : 'rotate(180deg)') : 'rotate(0deg)',
            transition: 'transform 0.15s ease'
          }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="searchable-schedule-popover"
          style={{
            position: 'absolute',
            ...(openUpwards ? { bottom: 'calc(100% + 5px)' } : { top: 'calc(100% + 5px)' }),
            right: 0,
            width: '420px',
            maxWidth: '90vw',
            backgroundColor: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '10px',
            boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.08)',
            zIndex: 9999,
            overflow: 'hidden'
          }}
        >
          {!isCreating ? (
            <>
              <div style={{ padding: '0.6rem 0.8rem', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search organisation by name or sector..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: '0.84rem',
                    width: '100%',
                    color: '#0f172a'
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem', padding: '0 4px' }}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {filteredOrgs.length === 0 ? (
                  <div style={{ padding: '1rem', fontSize: '0.82rem', color: '#64748b', textAlign: 'center' }}>
                    No matching organisation found for "{searchTerm}"
                  </div>
                ) : (
                  filteredOrgs.map(o => {
                    const orgId = o.id || o.OrganisationID;
                    const orgName = o.name || o.OrganisationName;
                    const orgSector = o.sector || o.Sector;
                    const isCurrent = String(currentOrg?.id) === String(orgId) || currentOrg?.name === orgName;

                    return (
                      <div
                        key={orgId || orgName}
                        onClick={() => {
                          onSelectOrg(rowId, orgId);
                          setIsOpen(false);
                        }}
                        style={{
                          padding: '0.65rem 0.85rem',
                          fontSize: '0.82rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: isCurrent ? '#f0fdf4' : 'transparent',
                          fontWeight: isCurrent ? 600 : 400,
                          transition: 'background-color 0.12s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isCurrent) e.currentTarget.style.backgroundColor = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span style={{
                          color: '#1e293b',
                          flex: 1,
                          lineHeight: 1.35,
                          wordBreak: 'break-word',
                          textAlign: 'left'
                        }}>
                          {orgName}
                        </span>
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontWeight: 600,
                          backgroundColor: orgSector === 'Government' ? '#dcfce7' : orgSector === 'Private' ? '#e0e7ff' : '#fef3c7',
                          color: orgSector === 'Government' ? '#15803d' : orgSector === 'Private' ? '#4338ca' : '#b45309',
                          flexShrink: 0
                        }}>
                          {orgSector}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Cannot find organisation? Create a new one */}
              <div
                onClick={() => {
                  setIsCreating(true);
                  setNewOrgName(searchTerm);
                  setNewOrgSector('Government');
                  setCreateError('');
                }}
                className="create-org-trigger-item"
              >
                <span className="create-org-icon-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </span>
                <span className="create-org-trigger-text">Cannot find organisation? <strong className="create-org-highlight">Create a new one</strong></span>
              </div>
            </>
          ) : (
            /* Inline Create New Organisation Form */
            <form onSubmit={handleCreateSubmit} className="create-org-inline-form">
              <div className="create-org-header-row">
                <div className="create-org-icon-badge header-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </div>
                <div className="create-org-heading">Create New Organisation</div>
              </div>

              {createError && (
                <div className="create-org-error-banner">
                  {createError}
                </div>
              )}

              <div className="create-org-field">
                <label className="create-org-label">
                  Organisation Name
                </label>
                <div className="create-org-input-wrapper">
                  <input
                    type="text"
                    placeholder="Enter organisation name (e.g. Petronas, Ministry of Health)"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    autoFocus
                    className="create-org-text-input"
                  />
                </div>
              </div>

              <div className="create-org-field">
                <label className="create-org-label">
                  Sector
                </label>
                <div className="create-org-select-wrapper">
                  <select
                    value={newOrgSector}
                    onChange={(e) => setNewOrgSector(e.target.value)}
                    className="create-org-sector-select"
                  >
                    <option value="Government">Government</option>
                    <option value="Private">Private</option>
                    <option value="Student">Student</option>
                  </select>
                  <svg
                    className="create-org-select-caret"
                    width="12"
                    height="12"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              <div className="create-org-actions-row">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="create-org-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="create-org-submit-btn"
                >
                  {createLoading ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="spinner-rotate">
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                        <path d="M12 2a10 10 0 0 1 10 10" strokeOpacity="1"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    'Save & Select'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};




const BulkRegistrationImport = ({ onBack, onImportSuccess, user }) => {
  const [stage, setStage] = useState('upload'); // 'upload' | 'review' | 'success'
  const [importMode, setImportMode] = useState('user'); // 'user' (New User Registration) | 'session' (Course Session Registration)
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [dragActive, setDragActive] = useState(false);

  // Configuration settings
  const [sendCredentialsEmail, setSendCredentialsEmail] = useState(true);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);

  // Staging data
  const [rows, setRows] = useState([]);
  const [masterData, setMasterData] = useState({
    organisations: [],
    courses: [],
    schedules: [],
    tags: []
  });
  const [summary, setSummary] = useState({
    totalRows: 0,
    ready: 0,
    newAccounts: 0,
    unmatchedOrg: 0,
    unmatchedSchedule: 0,
    duplicates: 0,
    clashes: 0,
    waitlisted: 0,
    fatalErrors: 0
  });

  // Filters & Search
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'ready' | 'review' | 'waitlist' | 'clash'
  const [searchQuery, setSearchQuery] = useState('');

  // Commit result state
  const [commitResult, setCommitResult] = useState(null);

  // Edit Row Modal State
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({
    fullName: '',
    email: '',
    contact: '',
    gender: 'Male',
    sector: 'Government',
    organisationName: '',
    position: '',
    supervisorName: '',
    supervisorEmail: '',
    scheduleId: '',
    projectCode: ''
  });
  const [editOrgSearch, setEditOrgSearch] = useState('');
  const [isEditOrgDropdownOpen, setIsEditOrgDropdownOpen] = useState(false);
  const [isCreatingNewOrgInModal, setIsCreatingNewOrgInModal] = useState(false);
  const editOrgDropdownRef = useRef(null);
  const [isGenderDropdownOpen, setIsGenderDropdownOpen] = useState(false);
  const genderDropdownRef = useRef(null);
  const [isSectorDropdownOpen, setIsSectorDropdownOpen] = useState(false);
  const sectorDropdownRef = useRef(null);
  const editEmailDomainTimerRef = useRef(null);

  // Close edit modal dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editOrgDropdownRef.current && !editOrgDropdownRef.current.contains(e.target)) {
        setIsEditOrgDropdownOpen(false);
      }
      if (genderDropdownRef.current && !genderDropdownRef.current.contains(e.target)) {
        setIsGenderDropdownOpen(false);
      }
      if (sectorDropdownRef.current && !sectorDropdownRef.current.contains(e.target)) {
        setIsSectorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fileInputRef = useRef(null);

  // ── Drag & Drop Handlers ──
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processSelectedFile(e.target.files[0]);
    }
  };

  const processSelectedFile = (selectedFile) => {
    setErrorMsg('');
    const fileName = selectedFile.name.toLowerCase();
    if (!fileName.endsWith('.csv') && selectedFile.type !== 'text/csv' && selectedFile.type !== 'application/vnd.ms-excel') {
      setErrorMsg('Please upload a valid CSV (.csv) file.');
      return;
    }
    if (selectedFile.size === 0) {
      setErrorMsg('The selected file is empty.');
      return;
    }
    setFile(selectedFile);
    parseFile(selectedFile);
  };

  const parseFile = async (fileToParse) => {
    setLoading(true);
    setErrorMsg('');

    try {
      const formData = new FormData();
      formData.append('file', fileToParse);
      formData.append('importMode', importMode);

      const res = await fetch(`${API_BASE_URL}/api/admin/registrations/bulk-preview`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        const detectedImportMode = data.detectedMode || importMode;
        const enrichedRows = (data.rows || []).map(r => {
          const isDup = detectedImportMode === 'user'
            ? Boolean(r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew))
            : Boolean(r.parsed.duplicate);
          const isEligible = r.status === 'READY' && !isDup;
          return {
            ...r,
            include: isEligible
          };
        });
        setRows(enrichedRows);
        setMasterData(data.masterData || { organisations: [], courses: [], schedules: [], tags: [] });
        setSummary(data.summary || {
          totalRows: enrichedRows.length,
          ready: 0,
          newAccounts: 0,
          unmatchedOrg: 0,
          unmatchedSchedule: 0,
          duplicates: 0,
          clashes: 0,
          waitlisted: 0,
          fatalErrors: 0
        });
        if (data.detectedMode && (data.detectedMode === 'user' || data.detectedMode === 'session')) {
          setImportMode(data.detectedMode);
        }
        setStage('review');
      } else {
        setErrorMsg(data.message || 'Failed to parse registration CSV file.');
      }
    } catch (err) {
      console.error('[Bulk Registration Import Error]:', err);
      setErrorMsg('Failed to communicate with the server. Ensure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  // ── Template Download ──
  const handleDownloadTemplate = (mode = importMode) => {
    window.open(`${API_BASE_URL}/api/admin/registrations/sample-template?type=${mode}`, '_blank');
  };

  // ── Staging Inline Handlers ──
  const handleToggleInclude = (rowId) => {
    setRows(prev =>
      prev.map(r => {
        if (r.rowId !== rowId) return r;
        const isDup = importMode === 'user'
          ? Boolean(r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew))
          : Boolean(r.parsed.duplicate);
        const cannotInclude = r.status !== 'READY' || isDup;
        if (cannotInclude) return r;
        return { ...r, include: !r.include };
      })
    );
  };

  const handleMasterToggle = (checked) => {
    setRows(prev =>
      prev.map(r => {
        const isDup = importMode === 'user'
          ? Boolean(r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew))
          : Boolean(r.parsed.duplicate);
        const cannotInclude = r.status !== 'READY' || isDup;
        return (cannotInclude ? r : { ...r, include: checked });
      })
    );
  };

  const handleOrgOverride = (rowId, orgValue) => {
    setRows(prev =>
      prev.map(r => {
        if (r.rowId !== rowId) return r;

        let updatedOrg;
        if (orgValue === 'NEW') {
          updatedOrg = {
            ...r.parsed.organisation,
            status: 'NEW_ORG',
            isNew: true,
            id: null,
            name: r.parsed.organisation.rawInput
          };
        } else {
          const selectedOrg = masterData.organisations.find(o => String(o.id) === String(orgValue));
          updatedOrg = {
            ...r.parsed.organisation,
            status: selectedOrg ? 'MATCHED' : (r.parsed.organisation.rawInput ? 'NEW_ORG' : 'UNMATCHED_ORG'),
            isNew: !selectedOrg && Boolean(r.parsed.organisation.rawInput),
            id: selectedOrg ? selectedOrg.id : null,
            name: selectedOrg ? selectedOrg.name : r.parsed.organisation.rawInput,
            sector: selectedOrg ? selectedOrg.sector : r.parsed.organisation.sector
          };
        }

        const updatedParsed = {
          ...r.parsed,
          organisation: updatedOrg
        };

        // Recalculate row warnings
        const updatedWarnings = r.warnings.filter(w => !w.toLowerCase().includes('organisation'));
        if (updatedOrg.status === 'UNMATCHED_ORG') {
          updatedWarnings.push('Organisation not selected.');
        }

        const isAccountOnly = updatedParsed.isAccountOnly;
        const newStatus = (r.errors && r.errors.length > 0)
          ? 'FATAL'
          : (updatedWarnings.length > 0 || updatedOrg.status === 'UNMATCHED_ORG' || (!updatedParsed.schedule && !isAccountOnly) ? 'WARNING' : 'READY');

        return {
          ...r,
          status: newStatus,
          warnings: updatedWarnings,
          parsed: updatedParsed
        };
      })
    );
  };

  const handleCreateAndSelectOrg = async (rowId, name, sector) => {
    const res = await fetch(`${API_BASE_URL}/api/organisations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, sector })
    });
    const result = await res.json();
    if (result.status !== 'success' && !result.data) {
      throw new Error(result.message || 'Failed to save organisation');
    }
    const newOrg = result.data;
    const formattedOrg = {
      id: newOrg.OrganisationID || newOrg.id,
      name: newOrg.OrganisationName || newOrg.name,
      sector: newOrg.Sector || newOrg.sector
    };

    // Update masterData list
    setMasterData(prev => ({
      ...prev,
      organisations: [...prev.organisations.filter(o => o.id !== formattedOrg.id), formattedOrg]
    }));

    // Update row
    setRows(prev =>
      prev.map(r => {
        if (r.rowId !== rowId) return r;
        const updatedOrg = {
          ...r.parsed.organisation,
          status: 'MATCHED',
          isNew: false,
          id: formattedOrg.id,
          name: formattedOrg.name,
          sector: formattedOrg.sector
        };
        const updatedParsed = {
          ...r.parsed,
          organisation: updatedOrg
        };
        const updatedWarnings = (r.warnings || []).filter(w => !w.toLowerCase().includes('organisation'));
        const isAccountOnly = updatedParsed.isAccountOnly;
        const newStatus = (r.errors && r.errors.length > 0)
          ? 'FATAL'
          : (updatedWarnings.length > 0 ? 'WARNING' : 'READY');

        return {
          ...r,
          status: newStatus,
          warnings: updatedWarnings,
          parsed: updatedParsed
        };
      })
    );
  };


  const handleScheduleOverride = (rowId, scheduleId) => {
    const isNone = scheduleId === 'none' || scheduleId === '' || scheduleId === null;
    const selectedSchedule = isNone ? null : masterData.schedules.find(s => String(s.scheduleId) === String(scheduleId));

    setRows(prev =>
      prev.map(r => {
        if (r.rowId !== rowId) return r;

        let updatedSchedule = null;
        let updatedCourse = { ...r.parsed.course };
        let updatedQuota = { ...r.parsed.quota };
        let isAccountOnly = false;

        if (isNone) {
          isAccountOnly = true;
          updatedCourse = {
            courseId: null,
            courseName: 'None (Account Only)',
            certificationBody: 'CCoE'
          };
          updatedQuota = {
            willWaitlist: false,
            priorityRank: null,
            proposedStatus: 'Account Only'
          };
        } else if (selectedSchedule) {
          isAccountOnly = false;
          updatedSchedule = {
            scheduleId: selectedSchedule.scheduleId,
            startDate: selectedSchedule.startDate,
            endDate: selectedSchedule.endDate,
            maxCapacity: selectedSchedule.maxCapacity,
            enrolledCount: selectedSchedule.enrolledCount
          };
          updatedCourse = {
            courseId: selectedSchedule.courseId,
            courseName: selectedSchedule.courseName,
            certificationBody: selectedSchedule.certificationBody || 'CCoE'
          };
          const isWaitlisted = (selectedSchedule.enrolledCount || 0) >= (selectedSchedule.maxCapacity || 10);
          updatedQuota = {
            willWaitlist: isWaitlisted,
            priorityRank: r.parsed.learner.sector === 'Government' ? 1 : 2,
            proposedStatus: isWaitlisted ? 'Waitlisted' : 'Pending Approval'
          };
        }

        // Dynamic Duplicate & Date Clash Re-evaluation
        const activeRegs = masterData.activeRegistrations || [];
        const learnerId = r.parsed.learner.learnerId;
        const foundDup = (selectedSchedule && learnerId)
          ? activeRegs.find(ar => ar.learnerId === learnerId && String(ar.scheduleId) === String(selectedSchedule.scheduleId))
          : null;
        const isDuplicate = Boolean(foundDup);

        // Remove old duplicate and schedule errors
        let updatedErrors = (r.errors || []).filter(e =>
          !e.toLowerCase().includes('already registered') &&
          !e.toLowerCase().includes('scheduleid is required') &&
          !e.toLowerCase().includes('could not be found in active courseschedule')
        );

        if (isDuplicate) {
          updatedErrors.push(`Learner is already registered for this class session (Status: ${foundDup.status || 'Active'}).`);
        }

        // Date Clash check if not duplicate
        let clashInfo = null;
        if (selectedSchedule && learnerId && !isDuplicate) {
          const targetStart = new Date(selectedSchedule.startDate).getTime();
          const targetEnd = new Date(selectedSchedule.endDate || selectedSchedule.startDate).getTime();
          for (const ar of activeRegs) {
            if (ar.learnerId === learnerId && ar.startDate && ar.endDate) {
              const regStart = new Date(ar.startDate).getTime();
              const regEnd = new Date(ar.endDate).getTime();
              if (targetStart <= regEnd && targetEnd >= regStart) {
                clashInfo = {
                  courseName: ar.courseName || 'Existing Course',
                  startDate: ar.startDate,
                  endDate: ar.endDate
                };
                break;
              }
            }
          }
        }

        let updatedWarnings = (r.warnings || []).filter(w =>
          !w.toLowerCase().includes('schedule') &&
          !w.toLowerCase().includes('course') &&
          !w.toLowerCase().includes('date clash') &&
          !w.toLowerCase().includes('capacity')
        );

        if (clashInfo) {
          updatedWarnings.push(`Date clash detected with active registration for "${clashInfo.courseName}" (${clashInfo.startDate} to ${clashInfo.endDate}).`);
        }
        if (updatedQuota.willWaitlist) {
          updatedWarnings.push(`Session capacity reached. Participant will be queued on waitlist.`);
        }

        const updatedParsed = {
          ...r.parsed,
          isAccountOnly,
          course: updatedCourse,
          schedule: updatedSchedule,
          quota: updatedQuota,
          duplicate: isDuplicate,
          clash: clashInfo
        };

        const isUnresolvedTag = Boolean(r.parsed.projectTag && r.parsed.projectTag.status === 'NOT_FOUND');
        const hasBlockingError = updatedErrors.length > 0;
        const hasWarning = updatedWarnings.length > 0 || updatedParsed.organisation.status === 'UNMATCHED_ORG' || (!updatedSchedule && !isAccountOnly) || isUnresolvedTag;
        const newStatus = hasBlockingError ? 'FATAL' : (hasWarning ? 'WARNING' : 'READY');

        return {
          ...r,
          status: newStatus,
          errors: updatedErrors,
          warnings: updatedWarnings,
          parsed: updatedParsed,
          include: newStatus === 'READY' && !isDuplicate
        };
      })
    );
  };

  const handleRemoveRow = (rowId) => {
    setRows(prev => prev.filter(r => r.rowId !== rowId));
  };

  // ── Open Edit Row Modal ──
  const handleOpenEditRow = (row) => {
    setEditingRow(row);
    const existingOrgName = row.parsed.organisation?.name === 'Unspecified Organisation' ? '' : (row.parsed.organisation?.name || '');
    const currentSector = row.parsed.organisation?.sector || row.parsed.learner?.sector || 'Government';
    setEditFormData({
      fullName: row.parsed.learner?.fullName || '',
      email: row.parsed.learner?.email || '',
      contact: row.parsed.learner?.contact || '',
      gender: row.parsed.learner?.gender || 'Male',
      sector: currentSector,
      organisationName: existingOrgName,
      position: row.parsed.learner?.position || '',
      supervisorName: row.parsed.supervisor?.name || '',
      supervisorEmail: row.parsed.supervisor?.email || '',
      scheduleId: row.parsed.schedule?.scheduleId ? String(row.parsed.schedule.scheduleId) : (row.parsed.isAccountOnly ? 'none' : ''),
      projectCode: row.parsed.projectTag?.raw || row.parsed.projectTag?.tagCode || ''
    });
    setEditOrgSearch('');
    setIsEditOrgDropdownOpen(false);
    setIsCreatingNewOrgInModal(false);
  };

  // ── Modal Org Selection Handler (Auto matches and syncs sector) ──
  const handleEditModalOrgSelect = (orgItem) => {
    if (orgItem === 'CREATE_NEW') {
      setIsCreatingNewOrgInModal(true);
      setEditFormData(prev => ({
        ...prev,
        organisationName: editOrgSearch.trim()
      }));
      setIsEditOrgDropdownOpen(false);
      return;
    }

    const orgName = orgItem.name || orgItem.OrganisationName || '';
    const orgSector = orgItem.sector || orgItem.Sector;

    setIsCreatingNewOrgInModal(false);
    setEditFormData(prev => ({
      ...prev,
      organisationName: orgName,
      // If organisation has a known sector, sync sector automatically!
      sector: orgSector && ['Government', 'Private', 'Student'].includes(orgSector) ? orgSector : prev.sector
    }));
    setEditOrgSearch('');
    setIsEditOrgDropdownOpen(false);
  };

  // ── Save Edited Row ──
  const handleSaveRowEdit = (e) => {
    e.preventDefault();
    if (!editingRow) return;

    setRows(prev => {
      const nextRows = prev.map(r => {
        if (r.rowId !== editingRow.rowId) return r;

        // ── In Session Mode: Update Schedule & Special Project Code ──
        if (importMode === 'session') {
          const selectedSchedId = editFormData.scheduleId;
          const isNone = selectedSchedId === 'none' || selectedSchedId === '' || selectedSchedId === null;
          const selectedSchedule = isNone ? null : (masterData.schedules || []).find(s => String(s.scheduleId) === String(selectedSchedId));

          let updatedSchedule = null;
          let updatedCourse = { ...r.parsed.course };
          let updatedQuota = { ...r.parsed.quota };
          let isAccountOnly = false;

          if (isNone) {
            isAccountOnly = true;
            updatedCourse = {
              courseId: null,
              courseName: 'None (Account Only)',
              certificationBody: 'CCoE'
            };
            updatedQuota = {
              willWaitlist: false,
              priorityRank: null,
              proposedStatus: 'Account Only'
            };
          } else if (selectedSchedule) {
            isAccountOnly = false;
            updatedSchedule = {
              scheduleId: selectedSchedule.scheduleId,
              startDate: selectedSchedule.startDate,
              endDate: selectedSchedule.endDate,
              maxCapacity: selectedSchedule.maxCapacity,
              enrolledCount: selectedSchedule.enrolledCount
            };
            updatedCourse = {
              courseId: selectedSchedule.courseId,
              courseName: selectedSchedule.courseName,
              certificationBody: selectedSchedule.certificationBody || 'CCoE'
            };
            const isWaitlisted = (selectedSchedule.enrolledCount || 0) >= (selectedSchedule.maxCapacity || 10);
            updatedQuota = {
              willWaitlist: isWaitlisted,
              priorityRank: r.parsed.learner?.sector === 'Government' ? 1 : 2,
              proposedStatus: isWaitlisted ? 'Waitlisted' : 'Pending Approval'
            };
          }

          // Project Tag re-evaluation
          let updatedProjectTag = null;
          const cleanProjectCode = (editFormData.projectCode || '').trim();
          const cleanTagUpper = cleanProjectCode.toUpperCase();
          const allTags = masterData.tags || [];
          let tagWarning = null;

          if (cleanProjectCode) {
            const foundTag = allTags.find(t => (t.code || t.TagCode || '').toUpperCase() === cleanTagUpper);
            if (foundTag) {
              const isTagActive = foundTag.isActive !== 0 && foundTag.IsActive !== 0;
              const expiryStr = foundTag.expiryDate || foundTag.ExpiryDate;
              let isExpired = false;
              if (expiryStr) {
                const expDate = new Date(expiryStr);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (expDate < today) isExpired = true;
              }

              if (!isTagActive || isExpired) {
                updatedProjectTag = {
                  tagId: foundTag.id || foundTag.UserTagID,
                  tagCode: foundTag.code || foundTag.TagCode,
                  tagName: foundTag.name || foundTag.TagName,
                  status: !isTagActive ? 'INACTIVE' : 'EXPIRED',
                  raw: cleanProjectCode
                };
                tagWarning = `Project code "${cleanProjectCode}" was not found in tag catalog.`;
              } else {
                updatedProjectTag = {
                  tagId: foundTag.id || foundTag.UserTagID,
                  tagCode: foundTag.code || foundTag.TagCode,
                  tagName: foundTag.name || foundTag.TagName,
                  status: 'VALID',
                  raw: cleanProjectCode
                };
              }
            } else {
              updatedProjectTag = {
                raw: cleanProjectCode,
                status: 'NOT_FOUND'
              };
              tagWarning = `Project code "${cleanProjectCode}" was not found in tag catalog.`;
            }
          }

          // Dynamic Duplicate & Date Clash Re-evaluation
          const activeRegs = masterData.activeRegistrations || [];
          const learnerId = r.parsed.learner?.learnerId;
          const foundDup = (selectedSchedule && learnerId)
            ? activeRegs.find(ar => ar.learnerId === learnerId && String(ar.scheduleId) === String(selectedSchedule.scheduleId))
            : null;
          const isDuplicate = Boolean(foundDup);

          let updatedErrors = (r.errors || []).filter(e =>
            !e.toLowerCase().includes('already registered') &&
            !e.toLowerCase().includes('scheduleid is required') &&
            !e.toLowerCase().includes('could not be found in active courseschedule') &&
            !e.toLowerCase().includes('is not an available class session')
          );

          if (isDuplicate) {
            updatedErrors.push(`Learner is already registered for this class session (Status: ${foundDup.status || 'Active'}).`);
          }

          let clashInfo = null;
          if (selectedSchedule && learnerId && !isDuplicate) {
            const targetStart = new Date(selectedSchedule.startDate).getTime();
            const targetEnd = new Date(selectedSchedule.endDate || selectedSchedule.startDate).getTime();
            for (const ar of activeRegs) {
              if (ar.learnerId === learnerId && ar.startDate && ar.endDate) {
                const regStart = new Date(ar.startDate).getTime();
                const regEnd = new Date(ar.endDate).getTime();
                if (targetStart <= regEnd && targetEnd >= regStart) {
                  clashInfo = {
                    courseName: ar.courseName || 'Existing Course',
                    startDate: ar.startDate,
                    endDate: ar.endDate
                  };
                  break;
                }
              }
            }
          }

          let updatedWarnings = (r.warnings || []).filter(w =>
            !w.toLowerCase().includes('schedule') &&
            !w.toLowerCase().includes('course') &&
            !w.toLowerCase().includes('date clash') &&
            !w.toLowerCase().includes('capacity') &&
            !w.toLowerCase().includes('project code')
          );

          if (clashInfo) {
            updatedWarnings.push(`Date clash detected with active registration for "${clashInfo.courseName}" (${clashInfo.startDate} to ${clashInfo.endDate}).`);
          }
          if (updatedQuota.willWaitlist) {
            updatedWarnings.push(`Session capacity reached. Participant will be queued on waitlist.`);
          }
          if (tagWarning) {
            updatedWarnings.push(tagWarning);
          }

          const updatedParsed = {
            ...r.parsed,
            isAccountOnly,
            course: updatedCourse,
            schedule: updatedSchedule,
            quota: updatedQuota,
            duplicate: isDuplicate,
            clash: clashInfo,
            projectTag: updatedProjectTag
          };

          const isUnresolvedTag = Boolean(updatedProjectTag && updatedProjectTag.status === 'NOT_FOUND');
          const hasBlockingError = updatedErrors.length > 0;
          const hasWarning = updatedWarnings.length > 0 || (!updatedSchedule && !isAccountOnly) || isUnresolvedTag;
          const newStatus = hasBlockingError ? 'FATAL' : (hasWarning ? 'WARNING' : 'READY');

          return {
            ...r,
            status: newStatus,
            errors: updatedErrors,
            warnings: updatedWarnings,
            parsed: updatedParsed,
            include: newStatus === 'READY' && !isDuplicate
          };
        }

        // ── In User Mode: Update Learner Information & Profile ──
        const cleanName = editFormData.fullName.trim();
        const cleanEmail = editFormData.email.trim().toLowerCase();
        const cleanContact = editFormData.contact.trim();
        const cleanGender = editFormData.gender;
        let cleanSector = editFormData.sector;
        const cleanOrgName = editFormData.organisationName.trim();
        const cleanPosition = editFormData.position.trim();
        const cleanSupName = editFormData.supervisorName.trim();
        const cleanSupEmail = editFormData.supervisorEmail.trim().toLowerCase();

        // Match or resolve organisation in masterData database
        const matchedOrg = (masterData.organisations || []).find(o =>
          (o.name || o.OrganisationName || '').toLowerCase() === cleanOrgName.toLowerCase()
        );

        // If matched with an existing organisation, enforce/reflect its sector!
        if (matchedOrg && matchedOrg.sector && ['Government', 'Private', 'Student'].includes(matchedOrg.sector)) {
          cleanSector = matchedOrg.sector;
        }

        // Recalculate warnings (Missing values -> Needs Review)
        const updatedWarnings = [];
        if (!cleanEmail || !cleanEmail.includes('@')) {
          updatedWarnings.push('Email address is missing or invalid.');
        }
        if (!cleanName) {
          updatedWarnings.push('Participant full name is missing.');
        }
        if (!cleanOrgName) {
          updatedWarnings.push('Organisation was not specified.');
        }
        if (!cleanContact) {
          updatedWarnings.push('Contact number is missing.');
        }
        if (!cleanGender) {
          updatedWarnings.push('Gender is missing.');
        }
        if (!cleanPosition) {
          updatedWarnings.push('Position / Job Title is missing.');
        }
        if (cleanSector === 'Government') {
          if (!cleanSupName) {
            updatedWarnings.push('Supervisor Name is missing for Government sector participant.');
          }
          if (!cleanSupEmail || !cleanSupEmail.includes('@')) {
            updatedWarnings.push('Supervisor Email is missing or invalid for Government sector participant.');
          }
        }

        const updatedOrg = {
          ...r.parsed.organisation,
          name: cleanOrgName || 'Unspecified Organisation',
          sector: cleanSector,
          id: matchedOrg ? (matchedOrg.id || matchedOrg.OrganisationID) : null,
          status: cleanOrgName ? (matchedOrg ? 'MATCHED' : 'NEW_ORG') : 'UNMATCHED_ORG'
        };

        const updatedSupervisor = {
          name: cleanSupName,
          email: cleanSupEmail,
          isValid: Boolean(cleanSupName && cleanSupEmail && cleanSupEmail.includes('@'))
        };

        // Username generation fallback
        const updatedUsername = cleanEmail ? cleanEmail.split('@')[0] : 'learner';

        // Check duplicate status dynamically against masterData.existingLearners
        const existingLearnerList = masterData.existingLearners || [];
        const isDbDuplicate = existingLearnerList.some(
          el => el.email === cleanEmail || el.username === cleanEmail
        );

        const updatedParsed = {
          ...r.parsed,
          duplicate: isDbDuplicate,
          learner: {
            ...r.parsed.learner,
            fullName: cleanName,
            email: cleanEmail,
            contact: cleanContact,
            gender: cleanGender,
            sector: cleanSector,
            position: cleanPosition,
            username: updatedUsername,
            isNew: !isDbDuplicate
          },
          organisation: updatedOrg,
          supervisor: updatedSupervisor
        };

        // If duplicate was resolved and errors were duplicate-related, clear duplicate error
        let updatedErrors = isDbDuplicate
          ? [`Account already exists (${cleanEmail}). Duplicated account cannot be imported.`]
          : [];

        // In user mode: duplicate = FATAL (Errors tab); warnings = WARNING (Needs Review tab); no warnings = READY
        const newStatus = isDbDuplicate
          ? 'FATAL'
          : (updatedWarnings.length > 0 ? 'WARNING' : 'READY');

        return {
          ...r,
          status: newStatus,
          errors: updatedErrors,
          warnings: updatedWarnings,
          parsed: updatedParsed,
          include: newStatus === 'READY'
        };
      });

      // Dynamically recalculate summary counts
      setSummary(prevSummary => ({
        ...prevSummary,
        totalRows: nextRows.length,
        ready: nextRows.filter(r => r.status === 'READY').length,
        newAccounts: nextRows.filter(r => r.parsed.learner?.isNew).length,
        duplicates: nextRows.filter(r => r.parsed.duplicate || r.status === 'FATAL').length,
        fatalErrors: nextRows.filter(r => r.status === 'FATAL').length
      }));

      return nextRows;
    });

    setEditingRow(null);
  };

  // ── Commit Bulk Import ──
  const handleCommitImport = async () => {
    const rowsToCommit = rows.filter(r => {
      const isDup = importMode === 'user'
        ? Boolean(r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew))
        : Boolean(r.parsed.duplicate);
      return r.include && r.status === 'READY' && !isDup;
    });

    if (rowsToCommit.length === 0) {
      setErrorMsg('No valid rows selected for import.');
      return;
    }

    setCommitLoading(true);
    setErrorMsg('');

    try {
      const payload = {
        rows: rowsToCommit,
        importMode,
        sendCredentialsEmail,
        sendConfirmationEmail,
        adminId: user?.adminId || user?.id || 1,
        adminName: user?.name || user?.username || 'Administrator',
        adminRole: user?.role || 'Admin'
      };

      const res = await fetch(`${API_BASE_URL}/api/admin/registrations/bulk-commit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        setCommitResult(data);
        setStage('success');
        if (onImportSuccess) {
          onImportSuccess(data);
        }
      } else {
        setErrorMsg(data.message || 'Failed to commit registration import.');
      }
    } catch (err) {
      console.error('[Bulk Registration Commit Error]:', err);
      setErrorMsg('Failed to communicate with server during commit.');
    } finally {
      setCommitLoading(false);
    }
  };

  // ── Row Filtering ──
  const filteredRows = rows.filter(r => {
    // Tab filter
    const isUserMode = importMode === 'user';
    const isDuplicateAccount = isUserMode && Boolean(r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew));
    const isUserReview = isUserMode && Boolean(!isDuplicateAccount && (r.status === 'WARNING' || (r.warnings && r.warnings.length > 0)));

    const isSessionError = !isUserMode && Boolean(r.status === 'FATAL' || r.parsed.duplicate || r.parsed.clash || (r.errors && r.errors.length > 0) || !r.parsed.learner.learnerId);
    const isSessionReview = !isUserMode && Boolean(!isSessionError && (r.status === 'WARNING' || (r.warnings && r.warnings.length > 0) || (!r.parsed.schedule && !r.parsed.isAccountOnly)));

    const isRowReady = isUserMode ? (!isDuplicateAccount && !isUserReview && r.status === 'READY') : (!isSessionError && !isSessionReview && r.status === 'READY');

    if (activeFilter === 'ready') {
      if (!isRowReady) return false;
    }
    if (activeFilter === 'review') {
      if (isUserMode ? !isUserReview : !isSessionReview) return false;
    }
    if (activeFilter === 'errors' || activeFilter === 'fatal') {
      if (isUserMode ? !isDuplicateAccount : !isSessionError) return false;
    }
    if (activeFilter === 'account_only' && !r.parsed.isAccountOnly) return false;
    if (activeFilter === 'new_accounts' && !r.parsed.learner.isNew) return false;
    if (activeFilter === 'clash' && (!r.parsed.clash && !r.parsed.duplicate && r.status !== 'FATAL')) return false;

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = (r.parsed.learner.fullName || '').toLowerCase();
      const email = (r.parsed.learner.email || '').toLowerCase();
      const org = (r.parsed.organisation.name || '').toLowerCase();
      const course = (r.parsed.course.courseName || '').toLowerCase();
      const pos = (r.parsed.learner.position || '').toLowerCase();
      const sup = (r.parsed.supervisor.name || '').toLowerCase();
      if (!name.includes(q) && !email.includes(q) && !org.includes(q) && !course.includes(q) && !pos.includes(q) && !sup.includes(q)) {
        return false;
      }
    }
    return true;
  });

  const selectedCount = rows.filter(r => {
    const isDup = importMode === 'user'
      ? Boolean(r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew))
      : Boolean(r.parsed.duplicate);
    return r.include && r.status === 'READY' && !isDup;
  }).length;

  return (
    <div className="bulk-reg-page-container">
      {/* ── Top Bar & Breadcrumb ── */}
      <header className="bulk-reg-top-bar">
        <div className="bulk-reg-top-bar-left">
          <nav className="bulk-reg-breadcrumb" aria-label="Breadcrumb">
            <button
              type="button"
              className="bulk-reg-breadcrumb-link"
              onClick={() => (onBack ? onBack() : null)}
            >
              Session Registration
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">External Registration Import</span>
          </nav>
          <h1 className="bulk-reg-header-title">
            <SvgIcon name="clipboard" size={26} />
            <span>Import Registrations (Google Forms & CSV)</span>
          </h1>
          <p className="bulk-reg-header-subtitle">
            Ingest participant lists directly from external spreadsheets and Google Forms with duplicate detection and waitlist governance.
          </p>
        </div>
        <div className="bulk-reg-top-bar-right">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => (onBack ? onBack() : null)}
          >
            Back to Session Registration
          </button>
        </div>
      </header>

      {/* ── Steps Indicator ── */}
      <div className="bulk-reg-steps-card">
        <div className={`bulk-reg-step-item ${stage === 'upload' ? 'active' : 'completed'}`}>
          <div className="bulk-reg-step-circle">1</div>
          <div className="bulk-reg-step-label">
            <span className="bulk-reg-step-title">Upload & Parse</span>
            <span className="bulk-reg-step-desc">CSV file & ingest settings</span>
          </div>
        </div>
        <div style={{ height: '2px', width: '40px', background: '#cbd5e1' }} />
        <div className={`bulk-reg-step-item ${stage === 'review' ? 'active' : (stage === 'success' ? 'completed' : 'inactive')}`}>
          <div className="bulk-reg-step-circle">2</div>
          <div className="bulk-reg-step-label">
            <span className="bulk-reg-step-title">Review & Normalization</span>
            <span className="bulk-reg-step-desc">Resolve mappings & dry-run</span>
          </div>
        </div>
        <div style={{ height: '2px', width: '40px', background: '#cbd5e1' }} />
        <div className={`bulk-reg-step-item ${stage === 'success' ? 'active' : 'inactive'}`}>
          <div className="bulk-reg-step-circle">3</div>
          <div className="bulk-reg-step-label">
            <span className="bulk-reg-step-title">Commit & Provision</span>
            <span className="bulk-reg-step-desc">Database transactions & dispatch</span>
          </div>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {errorMsg && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #f87171',
          borderRadius: '8px',
          padding: '1rem',
          color: '#991b1b',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <SvgIcon name="alertCircle" size={20} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STAGE 1: UPLOAD & INGESTION SETTINGS
          ══════════════════════════════════════════════════════════════ */}
      {stage === 'upload' && (
        <div className="bulk-reg-upload-card">
          {/* Two-Option Mode Selector */}
          <div>
            <label style={{ fontSize: '0.9rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '0.6rem' }}>
              Select Import Type & Template:
            </label>
            <div className="bulk-reg-solution-selector">
              {/* Option: New User Registration */}
              <div
                className={`solution-card ${importMode === 'user' ? 'active' : ''}`}
                onClick={() => setImportMode('user')}
                role="button"
                tabIndex={0}
                data-testid="mode-select-user"
              >
                <div className="solution-card-icon">
                  <SvgIcon name="users" size={22} />
                </div>
                <div className="solution-card-content">
                  <div className="solution-card-header">
                    <h3 className="solution-card-title">New User Registration</h3>
                    <span className="solution-card-badge">Account Provisioning</span>
                  </div>
                  <p className="solution-card-desc">
                    Batch onboard & provision learner accounts with full profile details (Full Name, Email, Contact, Sector, Organisation, Position, Supervisor).
                  </p>
                </div>
                <div className="solution-card-radio">
                  <input
                    type="radio"
                    name="importSolution"
                    checked={importMode === 'user'}
                    onChange={() => setImportMode('user')}
                  />
                </div>
              </div>

              {/* Option: Course Session Registration */}
              <div
                className={`solution-card ${importMode === 'session' ? 'active' : ''}`}
                onClick={() => setImportMode('session')}
                role="button"
                tabIndex={0}
                data-testid="mode-select-session"
              >
                <div className="solution-card-icon">
                  <SvgIcon name="calendar" size={22} />
                </div>
                <div className="solution-card-content">
                  <div className="solution-card-header">
                    <h3 className="solution-card-title">Course Session Registration</h3>
                    <span className="solution-card-badge">Session Enrollment</span>
                  </div>
                  <p className="solution-card-desc">
                    Batch enroll existing registered learners directly into course schedules with project codes (Email Address, ScheduleID, Special Project Code).
                  </p>
                </div>
                <div className="solution-card-radio">
                  <input
                    type="radio"
                    name="importSolution"
                    checked={importMode === 'session'}
                    onChange={() => setImportMode('session')}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sample Template Callout */}
          <div className="bulk-reg-template-download-card">
            <div>
              <p>
                <strong>
                  {importMode === 'user'
                    ? 'Need the New User Registration CSV template?'
                    : 'Need the Course Session Registration CSV template?'}
                </strong>
              </p>
              <p style={{ fontSize: '0.82rem', color: '#15803d' }}>
                {importMode === 'user'
                  ? 'Download the standardized template matching user registration fields (Full Name, Email Address, Phone Number, Gender, Sector, Organisation, Position, Supervisor Name, Supervisor Email).'
                  : 'Download the streamlined template matching session registration fields (Email Address, ScheduleID, Special Project Code).'}
              </p>
            </div>
            <a
              href={importMode === 'session'
                ? `${process.env.PUBLIC_URL || ''}/import_course_session_registration_template.csv`
                : `${process.env.PUBLIC_URL || ''}/import_new_user_registration_template.csv`}
              download={importMode === 'session'
                ? 'import_course_session_registration_template.csv'
                : 'import_new_user_registration_template.csv'}
              className="download-template-btn"
              data-testid="download-csv-template-btn"
              style={{ textDecoration: 'none' }}
              onClick={(e) => {
                if (API_BASE_URL) {
                  e.preventDefault();
                  handleDownloadTemplate(importMode);
                }
              }}
            >
              <SvgIcon name="download" size={16} />
              <span>
                {importMode === 'user' ? 'Download Sample CSV Template' : 'Download Sample CSV Template'}
              </span>
            </a>
          </div>

          {/* Dropzone */}
          <div
            className={`bulk-reg-dropzone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            data-testid="csv-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,application/vnd.ms-excel"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              data-testid="csv-file-input"
            />
            <div className="dropzone-icon-bubble">
              <SvgIcon name={importMode === 'user' ? 'users' : 'calendar'} size={32} />
            </div>
            <h3 className="dropzone-title">
              {importMode === 'user'
                ? 'Drag & drop your New User CSV file here'
                : 'Drag & drop your Course Session CSV file here'}
            </h3>
            <p className="dropzone-subtitle">
              {importMode === 'user'
                ? 'Supports new user onboarding sheets. Automatically provisions accounts and extracts full name, email, phone number, gender, sector, organization, position, and supervisor.'
                : 'Supports session enrollment sheets. Automatically matches email address to registered learners, checks ScheduleID against active class schedules, and applies special project codes.'}
            </p>
            <button
              type="button"
              className="dropzone-btn"
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation();
                if (fileInputRef.current) fileInputRef.current.click();
              }}
            >
              {loading ? 'Processing File...' : `Browse ${importMode === 'user' ? 'New User' : 'Course Session'} CSV File`}
            </button>
          </div>

          {/* Configuration Options */}
          <div className="bulk-reg-options-grid">
            <div className="bulk-reg-option-item" style={{ opacity: importMode === 'session' ? 0.6 : 1 }}>
              <input
                type="checkbox"
                id="sendCreds"
                checked={sendCredentialsEmail}
                disabled={importMode === 'session'}
                onChange={(e) => setSendCredentialsEmail(e.target.checked)}
              />
              <div>
                <label htmlFor="sendCreds" className="bulk-reg-option-label">
                  Auto-send credentials to new learners
                </label>
                <div className="bulk-reg-option-hint">
                  {importMode === 'session'
                    ? 'Muted for course session import (Learners already registered).'
                    : 'Dispatches welcome email with autogenerated username and temporary password. Prompts reset on first login.'}
                </div>
              </div>
            </div>

            <div className="bulk-reg-option-item" style={{ opacity: importMode === 'user' ? 0.6 : 1 }}>
              <input
                type="checkbox"
                id="sendConfirm"
                checked={sendConfirmationEmail}
                disabled={importMode === 'user'}
                onChange={(e) => setSendConfirmationEmail(e.target.checked)}
              />
              <div>
                <label htmlFor="sendConfirm" className="bulk-reg-option-label">
                  Auto-send registration confirmations
                </label>
                <div className="bulk-reg-option-hint">
                  {importMode === 'user'
                    ? 'Muted for new user import (No course sessions enrolled yet).'
                    : 'Notifies participants with class dates, venue details, and registration status acknowledgements.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STAGE 2: INTERACTIVE STAGING CONSOLE
          ══════════════════════════════════════════════════════════════ */}
      {stage === 'review' && (
        <>
          {/* Solution Mode Notice Banner */}
          <div className="bulk-reg-mode-banner">
            <div>
              <span>Active Import: </span>
              <span className="mode-pill-badge">
                <SvgIcon name={importMode === 'user' ? 'users' : 'calendar'} size={14} />
                <span>{importMode === 'user' ? 'New User Registration' : 'Course Session Registration'}</span>
              </span>
              <span style={{ marginLeft: '10px', fontSize: '0.8rem', color: '#64748b' }}>
                {importMode === 'user'
                  ? 'Reviewing learner accounts & profile data.'
                  : 'Reviewing session enrollments & class capacity.'}
              </span>
            </div>
          </div>

          {/* Top Metric Ribbon */}
          <div className="bulk-reg-metrics-ribbon">
            <div className="metric-card color-slate">
              <span className="metric-label">Total Detected</span>
              <span className="metric-value">{rows.length}</span>
            </div>
            {importMode === 'user' ? (
              <>
                <div className="metric-card color-green">
                  <span className="metric-label">Ready</span>
                  <span className="metric-value">
                    {rows.filter(r => {
                      const isDup = r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew);
                      const isWarn = r.status === 'WARNING' || (r.warnings && r.warnings.length > 0);
                      return !isDup && !isWarn && r.status === 'READY';
                    }).length}
                  </span>
                </div>
                <div className="metric-card color-blue">
                  <span className="metric-label">New Accounts</span>
                  <span className="metric-value">{rows.filter(r => r.parsed.learner.isNew).length}</span>
                </div>
                <div className="metric-card color-amber">
                  <span className="metric-label">Needs Review</span>
                  <span className="metric-value">
                    {rows.filter(r => {
                      const isDup = r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew);
                      return !isDup && (r.status === 'WARNING' || (r.warnings && r.warnings.length > 0));
                    }).length}
                  </span>
                </div>
                <div className="metric-card color-red">
                  <span className="metric-label">Errors</span>
                  <span className="metric-value">{rows.filter(r => r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew)).length}</span>
                </div>
              </>
            ) : (
              <>
                <div className="metric-card color-green">
                  <span className="metric-label">Ready</span>
                  <span className="metric-value">
                    {rows.filter(r => {
                      const isErr = r.status === 'FATAL' || r.parsed.duplicate || r.parsed.clash || (r.errors && r.errors.length > 0) || !r.parsed.learner.learnerId;
                      const isWarn = r.status === 'WARNING' || (r.warnings && r.warnings.length > 0) || (!r.parsed.schedule && !r.parsed.isAccountOnly);
                      return !isErr && !isWarn && r.status === 'READY';
                    }).length}
                  </span>
                </div>
                <div className="metric-card color-amber">
                  <span className="metric-label">Unmatched Schedule</span>
                  <span className="metric-value">{rows.filter(r => !r.parsed.schedule && !r.parsed.isAccountOnly).length}</span>
                </div>
                <div className="metric-card color-amber">
                  <span className="metric-label">Needs Review</span>
                  <span className="metric-value">
                    {rows.filter(r => {
                      const isErr = r.status === 'FATAL' || r.parsed.duplicate || r.parsed.clash || (r.errors && r.errors.length > 0) || !r.parsed.learner.learnerId;
                      const isWarn = r.status === 'WARNING' || (r.warnings && r.warnings.length > 0) || (!r.parsed.schedule && !r.parsed.isAccountOnly);
                      return !isErr && isWarn;
                    }).length}
                  </span>
                </div>
                <div className="metric-card color-red">
                  <span className="metric-label">Errors</span>
                  <span className="metric-value">{rows.filter(r => r.status === 'FATAL' || r.parsed.duplicate || r.parsed.clash || (r.errors && r.errors.length > 0) || !r.parsed.learner.learnerId).length}</span>
                </div>
              </>
            )}
          </div>

          {/* Controls Bar: Filters & Search */}
          <div className="bulk-reg-controls-card">
            <div className="bulk-reg-filter-tabs">
              <button
                type="button"
                className={`filter-tab-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                <span>All Rows</span>
                <span className="tab-count">{rows.length}</span>
              </button>
              {importMode === 'user' ? (
                <>
                  <button
                    type="button"
                    className={`filter-tab-btn ${activeFilter === 'errors' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('errors')}
                  >
                    <span>Errors</span>
                    <span className="tab-count">{rows.filter(r => r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew)).length}</span>
                  </button>
                  <button
                    type="button"
                    className={`filter-tab-btn ${activeFilter === 'review' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('review')}
                  >
                    <span>Needs Review</span>
                    <span className="tab-count">
                      {rows.filter(r => {
                        const isDup = r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew);
                        return !isDup && (r.status === 'WARNING' || (r.warnings && r.warnings.length > 0));
                      }).length}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`filter-tab-btn ${activeFilter === 'ready' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('ready')}
                  >
                    <span>Ready</span>
                    <span className="tab-count">
                      {rows.filter(r => {
                        const isDup = r.parsed.duplicate || (r.parsed.learner && !r.parsed.learner.isNew);
                        const isWarn = r.status === 'WARNING' || (r.warnings && r.warnings.length > 0);
                        return !isDup && !isWarn && r.status === 'READY';
                      }).length}
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className={`filter-tab-btn ${activeFilter === 'errors' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('errors')}
                  >
                    <span>Errors</span>
                    <span className="tab-count">{rows.filter(r => r.status === 'FATAL' || r.parsed.duplicate || r.parsed.clash || (r.errors && r.errors.length > 0) || !r.parsed.learner.learnerId).length}</span>
                  </button>
                  <button
                    type="button"
                    className={`filter-tab-btn ${activeFilter === 'review' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('review')}
                  >
                    <span>Needs Review</span>
                    <span className="tab-count">
                      {rows.filter(r => {
                        const isErr = r.status === 'FATAL' || r.parsed.duplicate || r.parsed.clash || (r.errors && r.errors.length > 0) || !r.parsed.learner.learnerId;
                        const isWarn = r.status === 'WARNING' || (r.warnings && r.warnings.length > 0) || (!r.parsed.schedule && !r.parsed.isAccountOnly);
                        return !isErr && isWarn;
                      }).length}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`filter-tab-btn ${activeFilter === 'ready' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('ready')}
                  >
                    <span>Ready</span>
                    <span className="tab-count">
                      {rows.filter(r => {
                        const isErr = r.status === 'FATAL' || r.parsed.duplicate || r.parsed.clash || (r.errors && r.errors.length > 0) || !r.parsed.learner.learnerId;
                        const isWarn = r.status === 'WARNING' || (r.warnings && r.warnings.length > 0) || (!r.parsed.schedule && !r.parsed.isAccountOnly);
                        return !isErr && !isWarn && r.status === 'READY';
                      }).length}
                    </span>
                  </button>
                </>
              )}
            </div>

            <div className="bulk-reg-search-box">
              <SvgIcon name="search" size={16} />
              <input
                type="text"
                placeholder="Search participant, email, org..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Staging Table */}
          <div className="bulk-reg-table-card">
            <div className="bulk-reg-table-wrapper">
              <table className="staging-table">
                <thead>
                  {importMode === 'user' ? (
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={
                            rows.filter(r => r.status === 'READY' && !r.parsed.duplicate && r.parsed.learner?.isNew).length > 0 &&
                            rows.filter(r => r.status === 'READY' && !r.parsed.duplicate && r.parsed.learner?.isNew).every(r => r.include)
                          }
                          disabled={rows.filter(r => r.status === 'READY' && !r.parsed.duplicate && r.parsed.learner?.isNew).length === 0}
                          onChange={(e) => handleMasterToggle(e.target.checked)}
                          aria-label="Toggle all ready rows"
                          title={rows.filter(r => r.status === 'READY' && !r.parsed.duplicate && r.parsed.learner?.isNew).length === 0 ? "No ready rows available to toggle" : "Toggle all ready rows"}
                        />
                      </th>
                      <th>Status</th>
                      <th>Participant Name & Email</th>
                      <th>Phone & Gender</th>
                      <th>Organisation & Sector</th>
                      <th>Position</th>
                      <th>Supervisor (Gov SLA)</th>
                      <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                  ) : (
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={
                            rows.filter(r => r.status === 'READY' && !r.parsed.duplicate).length > 0 &&
                            rows.filter(r => r.status === 'READY' && !r.parsed.duplicate).every(r => r.include)
                          }
                          disabled={rows.filter(r => r.status === 'READY' && !r.parsed.duplicate).length === 0}
                          onChange={(e) => handleMasterToggle(e.target.checked)}
                          aria-label="Toggle all ready rows"
                          title={rows.filter(r => r.status === 'READY' && !r.parsed.duplicate).length === 0 ? "No ready rows available to toggle" : "Toggle all ready rows"}
                        />
                      </th>
                      <th>Status</th>
                      <th>Learner (Matched Profile)</th>
                      <th>Target Course & Schedule</th>
                      <th>Special Project Code</th>
                      <th>Queue / Capacity</th>
                      <th style={{ textAlign: 'center' }}>Action</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                        No records match the current filter or search criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => {
                      const { parsed, status, errors, warnings } = row;
                      const isUnmatchedOrg = parsed.organisation.status === 'UNMATCHED_ORG';
                      const isNewOrg = parsed.organisation.status === 'NEW_ORG' || parsed.organisation.isNew;

                      if (importMode === 'user') {
                        // ── Render Row for New User Registration ──
                        return (
                          <tr
                            key={row.rowId}
                            className={status === 'FATAL' ? 'row-fatal' : (status === 'WARNING' ? 'row-warning' : '')}
                          >
                            {/* Include Checkbox */}
                            <td style={{ textAlign: 'center' }}>
                              {status === 'READY' && !parsed.duplicate && parsed.learner?.isNew ? (
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.include)}
                                    onChange={() => handleToggleInclude(row.rowId)}
                                    style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer' }}
                                    title="Click to include or exclude this ready participant from import"
                                    aria-label="Toggle include participant"
                                  />
                              ) : (
                                <div
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '4px',
                                    backgroundColor: '#e2e8f0',
                                    border: '1px solid #cbd5e1',
                                    color: '#94a3b8',
                                    cursor: 'not-allowed',
                                    userSelect: 'none'
                                  }}
                                  title={
                                    !parsed.learner.isNew || parsed.duplicate
                                      ? "Duplicated account cannot be imported"
                                      : status === 'WARNING'
                                      ? "Cannot be ticked: Resolve missing fields using the edit pen (✏️) until status is Ready"
                                      : "Cannot be ticked: Resolve errors first until status is Ready"
                                  }
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                </div>
                              )}
                            </td>

                            {/* Status Badge */}
                            <td>
                              {(!parsed.learner.isNew || parsed.duplicate) ? (
                                <span className="status-pill duplicate" title="Account already registered in system. Duplicated accounts cannot be imported.">
                                  Duplicated
                                </span>
                              ) : status === 'WARNING' || (warnings && warnings.length > 0) ? (
                                <span className="status-pill warning" style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d' }}>
                                  Needs Review
                                </span>
                              ) : (
                                <span className="status-pill new-user">New User</span>
                              )}
                              {(!parsed.learner.isNew || parsed.duplicate) ? (
                                <div style={{ fontSize: '0.72rem', color: '#be123c', marginTop: '4px', fontWeight: 500 }}>
                                  Duplicated account exists. Cannot proceed to import.
                                </div>
                              ) : (
                                <>
                                  {warnings && warnings.length > 0 && (
                                    <div style={{ fontSize: '0.72rem', color: '#b45309', marginTop: '4px' }}>
                                      {warnings[0]}
                                    </div>
                                  )}
                                  {errors && errors.length > 0 && (
                                    <div style={{ fontSize: '0.72rem', color: '#b91c1c', marginTop: '4px' }}>
                                      {errors[0]}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>

                            {/* Participant Name & Email */}
                            <td>
                              {parsed.learner.fullName ? (
                                <div style={{ fontWeight: 700, color: '#0f172a' }}>{parsed.learner.fullName}</div>
                              ) : (
                                <span style={{ color: '#b91c1c', fontSize: '0.82rem', fontWeight: 600, display: 'block' }}>
                                  Missing Full Name
                                </span>
                              )}
                              {parsed.learner.email ? (
                                <div style={{ fontSize: '0.8rem', color: '#475569' }}>{parsed.learner.email}</div>
                              ) : (
                                <span style={{ color: '#b91c1c', fontSize: '0.78rem', fontWeight: 600, display: 'block', marginTop: '2px' }}>
                                  Missing Email Address
                                </span>
                              )}
                              {parsed.learner.username && (
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                  Username: <code>{parsed.learner.username}</code>
                                </div>
                              )}
                            </td>

                            {/* Phone & Gender */}
                            <td>
                              <div style={{ fontSize: '0.82rem', color: '#1e293b', fontWeight: 600 }}>
                                {parsed.learner.contact || <span style={{ color: '#b91c1c' }}>Missing Contact</span>}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {parsed.learner.gender || <span style={{ color: '#b91c1c' }}>Missing Gender</span>}
                              </div>
                            </td>

                            {/* Organisation & Sector */}
                            <td>
                              {isUnmatchedOrg || !parsed.organisation.name || parsed.organisation.name === 'Unspecified Organisation' ? (
                                <div>
                                  <span style={{ color: '#b91c1c', fontSize: '0.82rem', fontWeight: 600, display: 'block' }}>
                                    Missing Organisation
                                  </span>
                                  {parsed.learner.sector && (
                                    <span style={{
                                      fontSize: '0.72rem',
                                      padding: '2px 6px',
                                      borderRadius: '6px',
                                      backgroundColor: parsed.learner.sector === 'Government' ? '#e0e7ff' : '#f1f5f9',
                                      color: parsed.learner.sector === 'Government' ? '#4338ca' : '#475569',
                                      fontWeight: 600,
                                      marginTop: '2px',
                                      display: 'inline-block'
                                    }}>
                                      {parsed.learner.sector}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{parsed.organisation.name}</span>
                                    {isNewOrg && (
                                      <span
                                        style={{
                                          fontSize: '0.68rem',
                                          fontWeight: 700,
                                          padding: '1px 5px',
                                          borderRadius: '4px',
                                          backgroundColor: '#fef3c7',
                                          color: '#b45309',
                                          border: '1px solid #fcd34d',
                                          letterSpacing: '0.02em'
                                        }}
                                        title="New organisation (will be automatically added to the database on import)"
                                      >
                                        New Org
                                      </span>
                                    )}
                                  </div>
                                  <span style={{
                                    fontSize: '0.72rem',
                                    padding: '2px 6px',
                                    borderRadius: '6px',
                                    backgroundColor: parsed.learner.sector === 'Government' ? '#e0e7ff' : '#f1f5f9',
                                    color: parsed.learner.sector === 'Government' ? '#4338ca' : '#475569',
                                    fontWeight: 600,
                                    marginTop: '2px',
                                    display: 'inline-block'
                                  }}>
                                    {parsed.learner.sector}
                                  </span>
                                </div>
                              )}
                            </td>

                            {/* Position */}
                            <td>
                              <span style={{ fontSize: '0.82rem', color: '#334155', fontWeight: 500 }}>
                                {parsed.learner.position || 'Participant'}
                              </span>
                            </td>

                            {/* Supervisor */}
                            <td>
                              {parsed.supervisor.isValid ? (
                                <div>
                                  <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>{parsed.supervisor.name}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{parsed.supervisor.email}</div>
                                </div>
                              ) : parsed.learner.sector === 'Government' ? (
                                <span style={{ color: '#b91c1c', fontSize: '0.78rem', fontWeight: 600 }}>
                                  Missing Supervisor
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>N/A (Private)</span>
                              )}
                            </td>

                            {/* Actions: Edit & Remove */}
                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <button
                                type="button"
                                onClick={() => handleOpenEditRow(row)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#4f46e5',
                                  cursor: 'pointer',
                                  padding: '4px 6px',
                                  marginRight: '4px',
                                  borderRadius: '4px',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                title="Edit participant details"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveRow(row.rowId)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#94a3b8',
                                  cursor: 'pointer',
                                  padding: '4px 6px',
                                  borderRadius: '4px',
                                  display: 'inline-flex',
                                  alignItems: 'center'
                                }}
                                title="Exclude row"
                              >
                                <SvgIcon name="trash" size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      }

                      // ── Render Row for Course Session Registration ──
                      const isSessionFatal = status === 'FATAL' || Boolean(parsed.duplicate) || Boolean(parsed.clash) || (!parsed.learner?.learnerId) || (errors && errors.length > 0);
                      return (
                        <tr
                          key={row.rowId}
                          className={isSessionFatal ? 'row-fatal' : (status === 'WARNING' ? 'row-warning' : '')}
                        >
                          {/* Include Checkbox */}
                          <td style={{ textAlign: 'center' }}>
                            {status === 'READY' && !parsed.duplicate ? (
                              <input
                                type="checkbox"
                                checked={Boolean(row.include)}
                                onChange={() => handleToggleInclude(row.rowId)}
                                style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer' }}
                                title="Click to include or exclude this ready participant from import"
                                aria-label="Toggle include participant"
                              />
                            ) : (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '18px',
                                  height: '18px',
                                  borderRadius: '4px',
                                  backgroundColor: '#e2e8f0',
                                  border: '1px solid #cbd5e1',
                                  color: '#94a3b8',
                                  cursor: 'not-allowed',
                                  userSelect: 'none'
                                }}
                                title={
                                  parsed.duplicate
                                    ? "Duplicated registration cannot be imported"
                                    : status === 'WARNING'
                                    ? "Cannot be ticked: Resolve review items first until status is Ready"
                                    : "Cannot be ticked: Resolve errors first until status is Ready"
                                }
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              </div>
                            )}
                          </td>

                          {/* Status Badge */}
                          <td>
                            {!parsed.learner.learnerId ? (
                              <span className="status-pill clash">User Not Found</span>
                            ) : parsed.duplicate ? (
                              <span className="status-pill duplicate">Duplicated Class Registration</span>
                            ) : parsed.clash ? (
                              <span className="status-pill clash" title={`Clash with ${parsed.clash.courseName}`}>Date Clash</span>
                            ) : parsed.quota && parsed.quota.isFull ? (
                              <span className="status-pill clash" title="Session capacity full">Capacity Full</span>
                            ) : status === 'FATAL' ? (
                              <span className="status-pill clash">Error</span>
                            ) : parsed.projectTag && parsed.projectTag.status === 'NOT_FOUND' ? (
                              <span className="status-pill warning" style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d' }}>Needs Review</span>
                            ) : status === 'WARNING' ? (
                              <span className="status-pill warning" style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d' }}>Needs Review</span>
                            ) : parsed.isAccountOnly ? (
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {parsed.learner.isNew && <span className="status-pill new-user">New User</span>}
                                <span className="status-pill account-only" title="Account Only - No Course Enrollment">Account Only</span>
                              </div>
                            ) : (
                              <span className="status-pill ready">Ready</span>
                            )}
                            {warnings && warnings.length > 0 && (
                              <div style={{ fontSize: '0.72rem', color: '#b45309', marginTop: '4px' }}>
                                {warnings[0]}
                              </div>
                            )}
                            {errors && errors.length > 0 && (
                              <div style={{ fontSize: '0.72rem', color: '#b91c1c', marginTop: '4px' }}>
                                {errors[0]}
                              </div>
                            )}
                          </td>

                          {/* Learner (Matched Profile) */}
                          <td>
                            {parsed.learner.learnerId ? (
                              <div>
                                <div style={{ fontWeight: 700, color: '#0f172a' }}>{parsed.learner.fullName}</div>
                                <div style={{ fontSize: '0.8rem', color: '#475569' }}>{parsed.learner.email}</div>
                                <div style={{ fontSize: '0.74rem', color: '#64748b', display: 'flex', gap: '6px', marginTop: '2px' }}>
                                  <span>{parsed.organisation.name || 'CCoE Participant'}</span>
                                  <span>•</span>
                                  <span>{parsed.learner.sector}</span>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontWeight: 700, color: '#b91c1c' }}>{parsed.learner.email}</div>
                                <div style={{ fontSize: '0.75rem', color: '#b91c1c' }}>Learner does not exist in the system</div>
                              </div>
                            )}
                          </td>

                          {/* Course & Schedule Override */}
                          <td>
                            <SearchableScheduleSelect
                              rowId={row.rowId}
                              schedule={parsed.schedule}
                              course={parsed.course}
                              schedules={masterData.schedules}
                              projectCode={parsed.projectTag?.raw || parsed.projectTag?.tagCode}
                              tags={masterData.tags}
                              courseTagAccess={masterData.courseTagAccess}
                              onChange={(selectedSchedId) => handleScheduleOverride(row.rowId, selectedSchedId)}
                            />
                          </td>

                          {/* Special Project Code */}
                          <td>
                            {parsed.projectTag && (parsed.projectTag.raw || parsed.projectTag.tagCode) ? (
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  backgroundColor: '#f1f5f9',
                                  color: '#334155',
                                  border: '1px solid #cbd5e1',
                                  padding: '3px 8px',
                                  borderRadius: '4px',
                                  fontWeight: 700,
                                  fontFamily: 'monospace',
                                  letterSpacing: '0.02em'
                                }}
                                title={parsed.projectTag.tagName || parsed.projectTag.raw || parsed.projectTag.tagCode}
                              >
                                {parsed.projectTag.raw || parsed.projectTag.tagCode}
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>None</span>
                            )}
                          </td>

                          {/* Queue / Capacity */}
                          <td>
                            {parsed.quota && parsed.quota.isFull ? (
                              <span style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 600 }}>
                                Enrolled (capacity {parsed.quota.current}/{parsed.quota.max})
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#15803d', fontWeight: 600 }}>
                                Enrolled (Capacity OK)
                              </span>
                            )}
                          </td>

                          {/* Actions: Edit & Remove */}
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <button
                              type="button"
                              onClick={() => handleOpenEditRow(row)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#4f46e5',
                                cursor: 'pointer',
                                padding: '4px 6px',
                                marginRight: '4px',
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                              title="Edit participant details"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(row.rowId)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#94a3b8',
                                cursor: 'pointer',
                                padding: '4px 6px',
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                              title="Exclude row"
                            >
                              <SvgIcon name="trash" size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sticky Action Footer */}
          <div className="bulk-reg-footer-bar">
            <div className="bulk-reg-footer-left">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setStage('upload');
                  setFile(null);
                  setRows([]);
                }}
              >
                Cancel & Upload New File
              </button>
              <span>
                <strong>{selectedCount}</strong> of {rows.length} record(s) ready to import
              </span>
            </div>

            <div className="bulk-reg-footer-right">
              <button
                type="button"
                className="btn-primary-commit"
                disabled={selectedCount === 0 || commitLoading}
                onClick={handleCommitImport}
                data-testid="commit-bulk-registrations-btn"
              >
                {commitLoading ? (
                  <>
                    <SvgIcon name="refresh" size={18} />
                    <span>Committing Transaction...</span>
                  </>
                ) : (
                  <>
                    <SvgIcon name="checkCircle" size={18} />
                    <span>
                      {importMode === 'user'
                        ? `Confirm & Provision User Accounts (${selectedCount})`
                        : `Confirm & Commit Import (${selectedCount})`}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              ROW EDIT MODAL (Session Mode vs User Mode)
              ══════════════════════════════════════════════════════════════ */}
          {editingRow && (
            <div className="bulk-reg-modal-backdrop" onClick={() => setEditingRow(null)}>
              <div className="bulk-reg-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="bulk-reg-modal-header">
                  <div>
                    <h3 className="bulk-reg-modal-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        {importMode === 'session' ? (
                          <>
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </>
                        ) : (
                          <>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </>
                        )}
                      </svg>
                      {importMode === 'session' ? 'Edit Course Session Registration' : 'Edit Learner Information'}
                    </h3>
                    <p className="bulk-reg-modal-subtitle">
                      {importMode === 'session'
                        ? 'Update or assign the course schedule and special project code for this participant.'
                        : 'Update missing values or adjust email to unblock duplicate accounts and enable direct import.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingRow(null)}
                    className="bulk-reg-modal-close"
                    title="Close modal"
                  >
                    &times;
                  </button>
                </div>

                <form onSubmit={handleSaveRowEdit}>
                  <div className="bulk-reg-modal-body">
                    {/* ════════ SESSION MODE FORM ════════ */}
                    {importMode === 'session' ? (
                      <>
                        {/* Learner Info Banner (Read-only for existing learner) */}
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px'
                        }}>
                          <div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>
                              Enrolling Participant
                            </div>
                            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
                              {editFormData.fullName || editingRow.parsed.learner?.fullName || 'Registered Learner'}
                            </div>
                            <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                              {editFormData.email || editingRow.parsed.learner?.email}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{
                              display: 'inline-block',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              backgroundColor: '#e0e7ff',
                              color: '#4338ca',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              {editingRow.parsed.organisation?.name || 'Registered Account'}
                            </div>
                            {editingRow.parsed.learner?.sector && (
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px', fontWeight: 500 }}>
                                Sector: {editingRow.parsed.learner.sector}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Schedule ID Selector */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Course Schedule <span style={{ color: '#dc2626' }}>*</span>
                          </label>
                          <SearchableScheduleSelect
                            rowId={`modal-${editingRow.rowId}`}
                            schedule={
                              editFormData.scheduleId && editFormData.scheduleId !== 'none'
                                ? (masterData.schedules || []).find(s => String(s.scheduleId) === String(editFormData.scheduleId))
                                : null
                            }
                            course={
                              editFormData.scheduleId && editFormData.scheduleId !== 'none'
                                ? (masterData.courses || []).find(c => {
                                    const s = (masterData.schedules || []).find(sch => String(sch.scheduleId) === String(editFormData.scheduleId));
                                    return s && s.courseId === c.id;
                                  })
                                : null
                            }
                            schedules={masterData.schedules}
                            projectCode={editFormData.projectCode}
                            tags={masterData.tags}
                            courseTagAccess={masterData.courseTagAccess}
                            onChange={(selectedSchedId) => {
                              setEditFormData(prev => ({
                                ...prev,
                                scheduleId: String(selectedSchedId)
                              }));
                            }}
                          />
                        </div>

                        {/* Special Project Code */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Special Project Code
                          </label>
                          <SearchableProjectTagSelect
                            value={editFormData.projectCode}
                            tags={masterData.tags}
                            onChange={(selectedCode) => {
                              const cleanCode = (selectedCode || '').trim().toUpperCase();
                              let shouldResetSchedule = false;

                              if (cleanCode && editFormData.scheduleId && editFormData.scheduleId !== 'none') {
                                const currentSched = (masterData.schedules || []).find(s => String(s.scheduleId) === String(editFormData.scheduleId));
                                const foundTag = (masterData.tags || []).find(t => (t.code || t.TagCode || '').toUpperCase() === cleanCode);
                                if (currentSched && foundTag) {
                                  const tagId = foundTag.id || foundTag.UserTagID;
                                  const allowedIds = (masterData.courseTagAccess || [])
                                    .filter(cta => String(cta.tagId || cta.UserTagID) === String(tagId))
                                    .map(cta => cta.courseId || cta.CourseID);
                                  if (!allowedIds.includes(currentSched.courseId)) {
                                    shouldResetSchedule = true;
                                  }
                                }
                              }

                              setEditFormData(prev => ({
                                ...prev,
                                projectCode: selectedCode,
                                ...(shouldResetSchedule ? { scheduleId: '' } : {})
                              }));
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {/* ════════ USER MODE FORM ════════ */}
                    {/* Full Name & Email */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                          Full Name <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={editFormData.fullName}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, fullName: e.target.value }))}
                          placeholder="e.g. John Doe"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                          Email Address <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={editFormData.email}
                          onChange={(e) => {
                            const newEmail = e.target.value;
                            setEditFormData(prev => ({ ...prev, email: newEmail }));

                            // Debounce domain lookup against database and masterData
                            if (editEmailDomainTimerRef.current) {
                              clearTimeout(editEmailDomainTimerRef.current);
                            }

                            if (newEmail.includes('@')) {
                              const domain = newEmail.split('@')[1]?.toLowerCase().trim();
                              if (domain && domain.includes('.') && !domain.endsWith('.')) {
                                editEmailDomainTimerRef.current = setTimeout(async () => {
                                  // 1. First check local masterData.organisations
                                  const localMatch = (masterData.organisations || []).find(o =>
                                    o.domain && o.domain.toLowerCase() === domain
                                  );
                                  if (localMatch) {
                                    handleEditModalOrgSelect(localMatch);
                                    return;
                                  }

                                  // 2. Query /api/organisations-by-domain to check full database
                                  try {
                                    const res = await fetch(`/api/organisations-by-domain/${encodeURIComponent(domain)}`);
                                    const data = await res.json();
                                    if (res.ok && data.status === 'success' && data.data && data.data.length > 0) {
                                      const matchedDbOrg = data.data[0];
                                      handleEditModalOrgSelect({
                                        id: matchedDbOrg.OrganisationID,
                                        name: matchedDbOrg.OrganisationName,
                                        sector: matchedDbOrg.Sector || data.sector,
                                        domain: matchedDbOrg.EmailDomain
                                      });
                                    }
                                  } catch (err) {
                                    console.warn('Domain lookup error:', err);
                                  }
                                }, 350);
                              }
                            }
                          }}
                          placeholder="e.g. john@example.com"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>

                    {/* Contact Number & Gender */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                          Contact Number
                        </label>
                        <input
                          type="text"
                          value={editFormData.contact}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, contact: e.target.value }))}
                          placeholder="e.g. 012-3456789"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      <div style={{ position: 'relative' }} ref={genderDropdownRef}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                          Gender
                        </label>
                        <div
                          className="bulk-reg-custom-dropdown-btn"
                          onClick={() => setIsGenderDropdownOpen(prev => !prev)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: isGenderDropdownOpen ? '1px solid #2563eb' : '1px solid #cbd5e1',
                            boxShadow: isGenderDropdownOpen ? '0 0 0 3px rgba(37, 99, 235, 0.14)' : 'none',
                            fontSize: '0.875rem',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontWeight: 500,
                            color: '#1e293b'
                          }}
                        >
                          <span>{editFormData.gender || 'Select Gender'}</span>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#64748b"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                              transform: isGenderDropdownOpen ? 'rotate(180deg)' : 'none',
                              transition: 'transform 0.2s ease'
                            }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>

                        {/* Gender Dropdown Menu */}
                        {isGenderDropdownOpen && (
                          <div
                            className="modal-dropdown-list"
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 4px)',
                              left: 0,
                              right: 0,
                              backgroundColor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              boxShadow: '0 10px 20px rgba(0, 0, 0, 0.12)',
                              zIndex: 1000,
                              overflow: 'hidden'
                            }}
                          >
                            {['Male', 'Female', 'Prefer not to say'].map(genderOpt => {
                              const isSelected = editFormData.gender === genderOpt;
                              return (
                                <div
                                  key={genderOpt}
                                  onClick={() => {
                                    setEditFormData(prev => ({ ...prev, gender: genderOpt }));
                                    setIsGenderDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '9px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                                    color: isSelected ? '#1d4ed8' : '#1e293b',
                                    fontWeight: isSelected ? 600 : 400,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#ffffff';
                                  }}
                                >
                                  <span>{genderOpt}</span>
                                  {isSelected && (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sector & Organisation (Auto-detecting & matching with DB) */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '16px', marginBottom: '14px' }}>
                      <div style={{ position: 'relative' }} ref={sectorDropdownRef}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                          Sector
                        </label>
                        <div
                          className="bulk-reg-custom-dropdown-btn"
                          onClick={() => setIsSectorDropdownOpen(prev => !prev)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: isSectorDropdownOpen ? '1px solid #2563eb' : '1px solid #cbd5e1',
                            boxShadow: isSectorDropdownOpen ? '0 0 0 3px rgba(37, 99, 235, 0.14)' : 'none',
                            fontSize: '0.875rem',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontWeight: 500,
                            color: '#1e293b'
                          }}
                        >
                          <span>{editFormData.sector}</span>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#64748b"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                              transform: isSectorDropdownOpen ? 'rotate(180deg)' : 'none',
                              transition: 'transform 0.2s ease'
                            }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>

                        {/* Sector Dropdown Menu */}
                        {isSectorDropdownOpen && (
                          <div
                            className="modal-dropdown-list"
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 4px)',
                              left: 0,
                              right: 0,
                              backgroundColor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              boxShadow: '0 10px 20px rgba(0, 0, 0, 0.12)',
                              zIndex: 1000,
                              overflow: 'hidden'
                            }}
                          >
                            {['Government', 'Private', 'Student'].map(sectorOpt => {
                              const isSelected = editFormData.sector === sectorOpt;
                              return (
                                <div
                                  key={sectorOpt}
                                  onClick={() => {
                                    setEditFormData(prev => ({ ...prev, sector: sectorOpt }));
                                    setIsSectorDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '9px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                                    color: isSelected ? '#1d4ed8' : '#1e293b',
                                    fontWeight: isSelected ? 600 : 400,
                                    borderBottom: '1px solid #f1f5f9'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#ffffff';
                                  }}
                                >
                                  <span>{sectorOpt}</span>
                                  {isSelected && (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div style={{ position: 'relative' }} ref={editOrgDropdownRef}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                          Organisation Name
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            value={isEditOrgDropdownOpen ? editOrgSearch : editFormData.organisationName}
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditOrgSearch(val);
                              setEditFormData(prev => ({ ...prev, organisationName: val }));
                              setIsEditOrgDropdownOpen(true);
                              setIsCreatingNewOrgInModal(false);

                              // If user typed an exact match from database, sync sector immediately!
                              const matched = (masterData.organisations || []).find(o =>
                                (o.name || o.OrganisationName || '').toLowerCase() === val.trim().toLowerCase()
                              );
                              if (matched && matched.sector) {
                                setEditFormData(prev => ({ ...prev, sector: matched.sector }));
                              }
                            }}
                            onFocus={() => {
                              setEditOrgSearch(editFormData.organisationName);
                              setIsEditOrgDropdownOpen(true);
                            }}
                            placeholder="Type to search or enter organisation..."
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>

                        {/* Searchable dropdown menu */}
                        {isEditOrgDropdownOpen && (
                          <div
                            className="modal-dropdown-list"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                              maxHeight: '220px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              marginTop: '4px'
                            }}
                          >
                            {(() => {
                              const term = (editOrgSearch || '').trim().toLowerCase();
                              const allOrgs = masterData.organisations || [];
                              const filteredOrgs = term
                                ? allOrgs.filter(o => (o.name || o.OrganisationName || '').toLowerCase().includes(term))
                                : allOrgs;

                              return (
                                <>
                                  {filteredOrgs.length > 0 ? (
                                    filteredOrgs.slice(0, 15).map(o => {
                                      const oName = o.name || o.OrganisationName;
                                      const isSelected = editFormData.organisationName.toLowerCase() === oName.toLowerCase();
                                      return (
                                        <div
                                          key={o.id || o.OrganisationID || oName}
                                          onClick={() => handleEditModalOrgSelect(o)}
                                          style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            fontSize: '0.84rem',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            borderBottom: '1px solid #f1f5f9',
                                            backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                                            color: isSelected ? '#1d4ed8' : '#1e293b'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : '#ffffff'}
                                        >
                                          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{oName}</span>
                                          {o.sector && (
                                            <span style={{
                                              fontSize: '0.72rem',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              backgroundColor: o.sector === 'Government' ? '#e0e7ff' : '#f1f5f9',
                                              color: o.sector === 'Government' ? '#4338ca' : '#475569',
                                              fontWeight: 600
                                            }}>
                                              {o.sector}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div style={{ padding: '8px 12px', color: '#64748b', fontSize: '0.82rem', textAlign: 'center' }}>
                                      No existing organisations matched in database.
                                    </div>
                                  )}

                                  {/* Create New Organisation option if typed term not in list */}
                                  {term && !allOrgs.some(o => (o.name || o.OrganisationName || '').toLowerCase() === term) && (
                                    <div
                                      onClick={() => handleEditModalOrgSelect('CREATE_NEW')}
                                      style={{
                                        padding: '9px 12px',
                                        cursor: 'pointer',
                                        fontSize: '0.84rem',
                                        borderTop: '1px solid #e2e8f0',
                                        backgroundColor: '#f8fafc',
                                        color: '#2563eb',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                      }}
                                    >
                                      <span>+ Create new organisation:</span>
                                      <span style={{ color: '#0f172a' }}>"{editOrgSearch}"</span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Position */}
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        Position / Job Title
                      </label>
                      <input
                        type="text"
                        value={editFormData.position}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, position: e.target.value }))}
                        placeholder="e.g. IT Officer / Executive"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>

                    {/* Supervisor Name & Email (Shown for Government sector) */}
                    {editFormData.sector === 'Government' && (
                      <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                          Supervisor Details (Required for Government Sector)
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                              Supervisor Name <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={editFormData.supervisorName}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, supervisorName: e.target.value }))}
                              placeholder="e.g. Dr. Ahmad"
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.875rem'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                              Supervisor Email <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                              type="email"
                              value={editFormData.supervisorEmail}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, supervisorEmail: e.target.value }))}
                              placeholder="e.g. ahmad@moh.gov.my"
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.875rem'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </div>

                  <div className="bulk-reg-modal-footer">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setEditingRow(null)}
                      style={{ padding: '8px 16px', borderRadius: '6px' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary-commit"
                      style={{ padding: '8px 20px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      <SvgIcon name="checkCircle" size={16} />
                      Save & Update Row
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════
          STAGE 3: SUCCESS REPORT
          ══════════════════════════════════════════════════════════════ */}
      {stage === 'success' && commitResult && (
        <div className="bulk-reg-success-card">
          <div className="success-icon-bubble">
            <SvgIcon name="checkCircle" size={42} />
          </div>
          <h2 className="success-title">
            {importMode === 'user'
              ? 'Bulk Registration Import Successful! (Learner Accounts Provisioned)'
              : 'Bulk Registration Import Successful! (Course Session Registration)'}
          </h2>
          <p className="success-subtitle">
            All records were ingested into the LMS database within an atomic transaction.
          </p>

          <div className="success-stats-grid">
            <div className="success-stat-item" style={{ minWidth: '260px', padding: '1.25rem 2rem' }}>
              <div className="success-stat-num">
                {importMode === 'session'
                  ? (commitResult.importedCount ?? 0)
                  : (commitResult.provisionedCount ?? commitResult.importedCount ?? 0)}
                <span style={{ fontSize: '1.15rem', fontWeight: 600, color: '#64748b', marginLeft: '6px' }}>
                  / {rows.length || commitResult.totalRows || commitResult.importedCount || 0}
                </span>
              </div>
              <div className="success-stat-lbl" style={{ marginTop: '0.4rem', fontSize: '0.85rem' }}>
                {importMode === 'session'
                  ? 'Class Session Registration / Total Records Imported from CSV'
                  : 'New Learner Accounts / Total Records Imported from CSV'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setStage('upload');
                setFile(null);
                setRows([]);
                setCommitResult(null);
              }}
            >
              Import Another File
            </button>
            <button
              type="button"
              className="btn-primary-commit"
              onClick={() => (onBack ? onBack() : null)}
            >
              Return to Session Registration
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkRegistrationImport;
